import { env } from '@/infra/env'
import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import { copy } from 'clipbrd'
import type {
  GenerateMemberMagicLinkFn,
  GetCurrentMemberFn,
  ImpersonateMemberFn,
} from '@/features/iam'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { Member } from '@/features/iam/members/Member'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { SearchableUser } from '@/features/iam/users/SearchableUser'
import type {
  AdminAccess,
  MembershipsTabDialogs,
} from '@/features/user-management/MembershipsTab/MembershipsTabViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for a row in the Memberships tab.
 */
export class MembershipItemViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    readonly membership: Membership,
    private readonly _user: () => SearchableUser,
    private readonly _selectedMembership: () => Membership | null,
    private readonly _toggleSelected: (membership: Membership) => void,
    private readonly _dialogs: MembershipsTabDialogs,
    private readonly _canPromoteToOwner: () => boolean,
    private readonly _impersonateMember: ImpersonateMemberFn,
    private readonly _generateMemberMagicLink: GenerateMemberMagicLinkFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Whether this membership is open in the sidebar.
   */
  @computed
  get isSelected() {
    return this._selectedMembership() === this.membership
  }

  /**
   * The current user's admin access in the membership's workspace.
   */
  @computed
  get access(): AdminAccess | null {
    let member: Member | null

    try {
      member = this._getCurrentMember(this.membership.workspace.id)
    } catch {
      return null
    }

    if (!member || member.inactive) {
      return null
    }

    return {
      canImpersonateAsClient: member.permissions.adminClientImpersonate,
      canImpersonateAsPro: member.permissions.adminProviderImpersonate,
      canAddUserToTeam: member.permissions.adminManageTeamMember,
      canEditUserRole: member.permissions.adminUserRolesModify,
      canEditUserSkills: member.permissions.adminSkillsManage,
      canManageUserCapacity: member.permissions.adminUserModify,
      canManageWorkspaceMemberships: member.permissions.adminUserModify,
    }
  }

  /**
   * Whether the current user can promote themselves to owner in the
   * membership's workspace.
   */
  @computed
  get canPromoteToOwner() {
    return (
      this._canPromoteToOwner() &&
      !this.membership.member.roles.some((r) => r.isOwnerRole)
    )
  }

  /**
   * Whether the membership's workspace signs the viewed customer in with
   * magic links instead of impersonation.
   */
  @computed
  get usesMagicLink() {
    return (
      this._user().isWorkspaceCustomer &&
      this.membership.workspace.config.externalCustomer.enabled &&
      this._getCurrentMember().user.platformAdmin
    )
  }

  /**
   * The name of the member's first named role.
   */
  @computed
  get roleName() {
    return this.membership.member.roles.find((r) => r.name)?.name
  }

  /**
   * Opens the membership in the sidebar, or closes the sidebar when it is
   * already open.
   */
  @action.bound
  view() {
    this._toggleSelected(this.membership)
  }

  @action.bound
  addToTeam() {
    this._dialogs.addUserToTeamVm.showForWorkspace(this.membership.workspace)
  }

  @action.bound
  editRole() {
    void this._dialogs.editUserRoleVm.showWithUser(
      this._user(),
      this.membership.workspace,
      this._lifecycle.signal,
    )
  }

  @action.bound
  editSkills() {
    void this._dialogs.manageSkillListVm.showWithUser(
      this._user(),
      this.membership.workspace,
      this._lifecycle.signal,
    )
  }

  @action.bound
  manageCapacity() {
    void this._dialogs.manageUserCapacityVm.showWithUser(
      this._user(),
      this.membership.workspace,
    )
  }

  @action.bound
  removeFromWorkspace() {
    this._dialogs.removeFromWorkspaceVm.showWithUser(
      this._user(),
      this.membership.workspace,
    )
  }

  @action.bound
  restore() {
    this._dialogs.restoreToWorkspaceVm.showWithUser(
      this._user(),
      this.membership.workspace,
    )
  }

  @action.bound
  promoteToOwner() {
    this._dialogs.promoteToOwnerVm.showForWorkspace(this.membership.workspace)
  }

  @action.bound
  impersonateAsClient() {
    this._impersonate('client').catch(() => undefined)
  }

  @action.bound
  impersonateAsPro() {
    this._impersonate('provider').catch(() => undefined)
  }

  /**
   * Generates a magic link for the member and copies it to the clipboard.
   */
  generateMagicLink = task.resolved(async () => {
    await this._generateMemberMagicLink({
      userPublicId: this.membership.member.user.publicId,
      workspaceId: this.membership.workspace.id,
      destination: { type: 'Dashboard' },
    })
      .then((result) => {
        const success = copy(result.url)
        this._newAlert()
          .withState(success ? 'success' : 'warning')
          .saying(
            success
              ? 'The Magic Link was copied to clipboard!'
              : 'Oh, seems like your browser does not support copying to the clipboard.',
          )
          .show()
          .autoDismiss()
      })
      .catch(this._createAlertErrorHandler('Generating the magic link failed'))
  })

  private async _impersonate(target: 'client' | 'provider') {
    const workspace = this.membership.workspace
    const domain = workspace.domains[0]?.domain
    const waitFn = `document.write('Please wait, generating impersonation URL.')`
    const newTab = window.open('javascript:' + waitFn, '_blank')

    const clientPortalBase = domain
      ? `${window.location.protocol}//${domain}`
      : env.CLIENT_PORTAL_URL
    const clientPortal = `${clientPortalBase}/auth/callback`
    const portal =
      target === 'client' ? clientPortal : env.IMPERSONATED_PROVIDER_PORTAL_URL

    await this._impersonateMember(this._user().publicId, workspace.id, portal)
      .then((result) => {
        newTab?.location.assign(result.url)
      })
      .catch((err: unknown) => {
        newTab?.close()
        this._createAlertErrorHandler('Impersonating failed')(err)
      })
  }
}
